/* -----------------Third parties--------------- */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePickerModal, TimePickerModal } from "react-native-paper-dates";
import { getDatePickerLocale } from "../../helpers/date-picker-locale";

interface DateTimePickerModalProps {
	isVisible: boolean;
	mode?: "date" | "datetime";
	onConfirm: (date: Date) => void;
	onCancel: () => void;
	date?: Date;
	minimumDate?: Date;
	label?: string;
	saveLabel?: string;
}

const DateTimePickerModal = ({
	isVisible,
	onConfirm,
	onCancel,
	date,
	mode = "datetime",
	minimumDate,
	label,
	saveLabel,
}: DateTimePickerModalProps) => {
	const { t, i18n } = useTranslation();
	const [showDatePicker, setShowDatePicker] = useState(true);
	const [showTimePicker, setShowTimePicker] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(date);

	const handleDateConfirm = (params: unknown) => {
		const singleParams = params as { date?: Date };
		if (singleParams.date) {
			setSelectedDate(singleParams.date);
			setShowDatePicker(false);
			if (mode === "datetime") {
				setShowTimePicker(true);
			} else {
				onConfirm(singleParams.date);
			}
		}
	};

	const handleTimeConfirm = ({
		hours,
		minutes,
	}: { hours: number; minutes: number }) => {
		if (selectedDate) {
			const finalDateTime = new Date(selectedDate);
			finalDateTime.setHours(hours, minutes, 0, 0);
			onConfirm(finalDateTime);
		}
		if (mode === "datetime") {
			setShowDatePicker(true);
		}
		setShowTimePicker(false);
	};

	const handleCancel = () => {
		setShowDatePicker(true);
		setShowTimePicker(false);
		onCancel();
	};

	if (!isVisible) {
		return null;
	}

	const getDefaultSaveLabel = () => {
		if (saveLabel) {
			return saveLabel;
		}
		return mode === "date"
			? t("common.datePicker.setDateCta")
			: t("common.datePicker.nextCta");
	};

	const pickerLocale = getDatePickerLocale(i18n.language);

	return (
		<>
			<DatePickerModal
				visible={showDatePicker}
				onDismiss={handleCancel}
				onConfirm={handleDateConfirm}
				date={selectedDate}
				mode="single"
				locale={pickerLocale}
				label={label ?? t("common.datePicker.title")}
				saveLabel={getDefaultSaveLabel()}
				validRange={{ startDate: minimumDate }}
				startYear={minimumDate?.getFullYear()}
			/>
			{mode === "datetime" && (
				<TimePickerModal
					visible={showTimePicker}
					use24HourClock={false}
					onDismiss={handleCancel}
					locale={pickerLocale}
					cancelLabel={t("common.cancel")}
					confirmLabel={t("common.datePicker.setTimeCta")}
					onConfirm={handleTimeConfirm}
					hours={selectedDate ? selectedDate.getHours() : 14}
					minutes={selectedDate ? selectedDate.getMinutes() : 30}
				/>
			)}
		</>
	);
};

export default DateTimePickerModal;
